
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ApiEndpoint, useCreateApiEndpoint, useUpdateApiEndpoint } from '@/hooks/useApiProviders';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2 } from 'lucide-react';

interface EndpointFormProps {
  providerId: string;
  endpoint?: ApiEndpoint;
  onSuccess?: () => void;
}

const formSchema = z.object({
  provider_id: z.string(),
  name: z.string().min(2, { message: 'Name must be at least 2 characters' }),
  path: z.string().min(1, { message: 'Path is required' }),
  method: z.string().min(1, { message: 'Method is required' }),
  description: z.string().optional().or(z.literal('')),
  request_schema: z.any().optional(),
  response_schema: z.any().optional(),
  is_active: z.boolean().default(true),
});

export function EndpointForm({ providerId, endpoint, onSuccess }: EndpointFormProps) {
  const { mutate: createEndpoint, isPending: isCreating } = useCreateApiEndpoint();
  const { mutate: updateEndpoint, isPending: isUpdating } = useUpdateApiEndpoint();
  const [activeTab, setActiveTab] = useState('basic');
  
  const isPending = isCreating || isUpdating;
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: endpoint ? {
      ...endpoint,
      provider_id: providerId,
      description: endpoint.description || '',
      request_schema: endpoint.request_schema || {},
      response_schema: endpoint.response_schema || {},
      is_active: endpoint.is_active !== false,
    } : {
      provider_id: providerId,
      name: '',
      path: '',
      method: 'GET',
      description: '',
      request_schema: {},
      response_schema: {},
      is_active: true,
    },
  });
  
  const [requestSchema, setRequestSchema] = useState<string>(
    endpoint?.request_schema ? JSON.stringify(endpoint.request_schema, null, 2) : '{}'
  );
  
  const [responseSchema, setResponseSchema] = useState<string>(
    endpoint?.response_schema ? JSON.stringify(endpoint.response_schema, null, 2) : '{}'
  );

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    // Parse JSON schemas or use empty objects on failure
    let parsedRequestSchema = {};
    let parsedResponseSchema = {};
    
    try {
      if (requestSchema && requestSchema.trim() !== '') {
        parsedRequestSchema = JSON.parse(requestSchema);
      }
    } catch (e) {
      console.error('Invalid request schema JSON', e);
    }
    
    try {
      if (responseSchema && responseSchema.trim() !== '') {
        parsedResponseSchema = JSON.parse(responseSchema);
      }
    } catch (e) {
      console.error('Invalid response schema JSON', e);
    }
    
    // Ensure all required fields are present
    const submissionData: Omit<ApiEndpoint, 'id'> = {
      provider_id: providerId,
      name: data.name,
      path: data.path,
      method: data.method,
      description: data.description,
      request_schema: parsedRequestSchema,
      response_schema: parsedResponseSchema,
      is_active: data.is_active
    };
    
    if (endpoint) {
      updateEndpoint({
        id: endpoint.id,
        ...submissionData,
      }, {
        onSuccess: () => {
          if (onSuccess) onSuccess();
        },
      });
    } else {
      createEndpoint(submissionData, {
        onSuccess: () => {
          if (onSuccess) onSuccess();
        },
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="request">Request Schema</TabsTrigger>
            <TabsTrigger value="response">Response Schema</TabsTrigger>
          </TabsList>
          
          <TabsContent value="basic" className="pt-4 space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Endpoint Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Get Clients" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              <FormField
                control={form.control}
                name="path"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Path</FormLabel>
                    <FormControl>
                      <Input placeholder="/api/resource" {...field} />
                    </FormControl>
                    <FormDescription>
                      Relative to the provider's base URL
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="method"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>HTTP Method</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="GET">GET</SelectItem>
                        <SelectItem value="POST">POST</SelectItem>
                        <SelectItem value="PUT">PUT</SelectItem>
                        <SelectItem value="PATCH">PATCH</SelectItem>
                        <SelectItem value="DELETE">DELETE</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Describe what this endpoint does" 
                      className="resize-none" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Allow users to access this endpoint
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </TabsContent>
          
          <TabsContent value="request" className="pt-4">
            <FormItem>
              <FormLabel>Request Schema (JSON)</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="{}" 
                  className="font-mono h-[300px]"
                  value={requestSchema}
                  onChange={(e) => setRequestSchema(e.target.value)}
                />
              </FormControl>
              <FormDescription>
                Define the expected request parameters in JSON format
              </FormDescription>
            </FormItem>
          </TabsContent>
          
          <TabsContent value="response" className="pt-4">
            <FormItem>
              <FormLabel>Response Schema (JSON)</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="{}" 
                  className="font-mono h-[300px]"
                  value={responseSchema}
                  onChange={(e) => setResponseSchema(e.target.value)}
                />
              </FormControl>
              <FormDescription>
                Define the expected response format in JSON format
              </FormDescription>
            </FormItem>
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => {
            if (activeTab === 'basic') {
              setActiveTab('request');
            } else if (activeTab === 'request') {
              setActiveTab('response');
            } else {
              form.handleSubmit(onSubmit)();
            }
          }}>
            {activeTab === 'response' ? 'Save' : 'Next'}
          </Button>
          
          {activeTab === 'response' && (
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {endpoint ? 'Update Endpoint' : 'Create Endpoint'}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
